<template>
  <div class="w-full h-full flex-col min-h-[350px]">
    <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute rounded-t-lg left-0 top-0 pointer-events-none" />
    <div class="flex items-center w-full">
      <PlaybookSequenceIcon class="w-12 h-12 -ml-2" color="#000" />
      <span class="mt-1 ml-2 text-3xl font-bold text-black">{{ !playbook ? $t("Create New Playbook") : $t("Update Playbook") }}</span>
      <div
        class="flex items-center justify-center w-8 h-8 ml-auto mr-3 bg-white border rounded-full cursor-pointer has-tooltip dark:border-dark-divider-border dark:bg-dark-screen-background"
        @click="$emit('cancel')">
        <X color="#FF0000" class="w-6 h-6 transition ease-in-out duration-side-menu-transition-duration" /><span
          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
          {{ $t("Close") }}</span>
      </div>
    </div>
    <div class="text-common-grey">
      {{ $t("Automate your workflow") }}
    </div>

    <div class="flex items-center justify-center my-2 mt-10">
      <TextInput
        v-model="playbook_name"
        :value="playbook_name"
        class="w-full"
        :is-box-background="true"
        :show-label="true"
        :auto-focus="false"
        input-classes="appearance-none block px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light bg-white focus:border-blue-light sm:text-sm"
        name="name"
        type="text"
        :placeholder="$t('Name Your Playbook')"
        :label="$t('Name Your Playbook')"
        @input="playbook_name = $event.target.value" />

      <div class="flex flex-row items-center gap-2 ml-2">
        <label class="block text-base font-medium text-gray-700 dark:text-dark-body-text">{{ $t("Private") }} </label>
        <Switch
          v-model="is_public"
          :class="[
            !is_public ? 'bg-blue' : 'bg-gray-200 dark:bg-dark-screen-background',
            'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
          ]">
          <span
            aria-hidden="true"
            :class="[
              !is_public && $store.state.portfolio.ui_options?.language == 'ar'
                ? '-translate-x-5'
                : !is_public
                  ? 'translate-x-5'
                  : 'translate-x-0',
              'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
            ]" />
        </Switch>
      </div>
    </div>
    <div v-if="playbooks?.map((p) => p.name)?.includes(playbook_name)" class="text-red-500 text-sm">
      {{ $t("Playbook name already taken") }}
    </div>
    <TextArea
      classes="w-full mt-2"
      input-classes="dark:bg-dark-box-background bg-light-box-background dark:text-dark-body-text px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-md"
      show-label
      :label="$t('Description')"
      name="description"
      :value="description"
      @input="description = $event.target.value" />

    <span class="mt-3">{{ $t("Add a tag to your Playbook") }}</span>

    <div class="flex flex-row items-center w-full gap-2 mt-2">
      <TextInput
        v-model="tag_name"
        :only-alpha-numeric="false"
        :value="tag_name"
        class="w-[270px] max-w-[270px]"
        :is-box-background="true"
        :show-label="false"
        :auto-focus="false"
        input-classes="appearance-none block px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light bg-white focus:border-blue-light sm:text-sm"
        name="name"
        type="text"
        :placeholder="`${playbook_tags.length ? $t('Search or create tag') : $t('Name your tag')}`"
        label=""
        @input="tag_name = $event.target.value" />
      <div v-if="tag_name" class="h-10 w-10 !p-0 flex items-center rounded-md border-none">
        <TextInput
          :value="new_tag.color"
          label=""
          classes="h-10 w-10 !p-0 flex items-center rounded-md border-none"
          input-classes="h-10 w-10 overflow-hidden appearance-none rounded-md !p-0 border-none"
          name="color"
          type="color"
          :placeholder="$t('Tag Color')"
          @input="new_tag.color = $event.target.value" />
      </div>
      <button
        v-if="tag_name"
        :disabled="is_name_duplicated"
        class="primary-btn w-fit"
        @click="tag_to_be_edited ? editTag() : createAndAssignTag()">
        <span>{{ tag_to_be_edited ? $t("Edit tag") : $t("Create and assign") }}</span>
        <Check class="w-6 h-6 ml-2" aria-hidden="true" />
      </button>
      <button
        v-if="tag_to_be_edited"
        type="button"
        class="w-fit cancel-btn py-2.5 -ml-1"
        @click="
          () => {
            tag_to_be_edited = null
            tag_name = ''
          }
        ">
        <span>{{ $t("Cancel") }}</span>
      </button>
    </div>

    <div v-if="playbook_tags.length == 0" class="flex justify-center w-full my-2 mt-32 text-black">
      {{ $t("You currently have no tags created. Use the input box above to create your first tag.") }}
    </div>

    <div v-else class="flex flex-wrap gap-2 mt-4">
      <div
        v-for="tag in all_tags"
        :key="tag?.id"
        class="flex focus:outline-none"
        :style="{
          background: playbook_tags.find((t) => t.id == tag)?.color
        }">
        <div v-if="selected_tags.includes(tag.id)" class="flex items-center group">
          <span
            class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
            :style="{
              background: playbook_tags.find((t) => t.id == tag.id)?.color
            }">
            <Check class="block w-4 h-4 mr-2 text-white cursor-pointer group-hover:hidden" aria-hidden="true" />
            <X
              class="hidden w-4 h-4 mr-2 text-white cursor-pointer group-hover:block"
              aria-hidden="true"
              @click="(e) => handleSelectTag(false, tag)" />
            <span class="block text-white truncate">{{ tag.name }}</span>
          </span>
          <Pencil
            color="#899399"
            class="hidden w-5 h-5 ml-2 cursor-pointer group-hover:block"
            aria-hidden="true"
            @click="
              () => {
                tag_to_be_edited = tag
                tag_name = tag.name
                new_tag.color = tag.color
              }
            " />
          <Trash2
            class="hidden w-6 h-6 ml-2 text-red-500 cursor-pointer group-hover:block"
            aria-hidden="true"
            @click="
              () => {
                tag_to_be_deleted = tag
                is_delete_tags_modal_open = true
              }
            " />
        </div>
        <div v-else class="flex items-center group">
          <span class="flex items-center rounded-md px-2 py-1.5 border border-gray-300">
            <div class="mr-3 w-3.5 h-3.5 rounded-sm" :style="{ background: tag.color }">
              <Check
                class="w-3.5 h-3.5 text-white hidden group-hover:block cursor-pointer"
                aria-hidden="true"
                @click="(e) => handleSelectTag(true, tag)" />
            </div>
            <span class="block text-black truncate">{{ tag.name }}</span>
          </span>
          <Pencil
            color="#899399"
            class="hidden w-5 h-5 ml-2 cursor-pointer group-hover:block"
            aria-hidden="true"
            @click="
              () => {
                tag_to_be_edited = tag
                tag_name = tag.name
                new_tag.color = tag.color
              }
            " />
          <Trash2
            class="hidden w-6 h-6 ml-2 text-red-500 cursor-pointer group-hover:block"
            aria-hidden="true"
            @click="
              () => {
                tag_to_be_deleted = tag
                is_delete_tags_modal_open = true
              }
            " />
        </div>
      </div>
    </div>

    <div class="flex flex-row-reverse items-center w-full gap-2 mt-12">
      <button
        :disabled="!playbook_name || playbooks?.map((p) => p.name)?.includes(playbook_name)"
        class="primary-btn w-fit"
        @click="startSetup()">
        <span>{{ !playbook ? $t("Start Setup") : $t("Update") }}</span>
        <Check class="w-6 h-6 ml-2" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="w-fit cancel-btn py-2.5 -ml-1"
        @click="
          () => {
            $emit('cancel')
          }
        ">
        <span>{{ $t("Cancel") }}</span>
      </button>
    </div>
  </div>
  <!-- Delete Tags -->
  <Modal
    v-model:is-open="is_delete_tags_modal_open"
    max-width="max-w-[600px] mx-5"
    :use-toast="true"
    :default-success-msg="$t('Tag deleted successfully')">
    <template #form="{ close: closeModal, done: showIsDone }">
      <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        {{ $t("Delete Tag") }}
      </h3>
      <div class="max-w-xl mt-2 text-sm text-gray-500">
        <p>{{ $t("Once you delete tag, you can't restore it.") }}</p>
      </div>
      <div class="mt-5 text-center">
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
          @click="confirmDeleteTag(showIsDone)">
          {{ $t("Delete Tag") }}
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
          @click="closeModal">
          {{ $t("Cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>
<script>
import playbookService from "@/services/playbook.service"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import { Switch } from "@headlessui/vue"
import Modal from "@/components/shared/Modal.vue"
import PlaybookSequenceIcon from "@/components/shared/icons/PlaybookSequenceIcon.vue"
import { X, Check, Trash2, Pencil } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    TextArea,
    Switch,
    PlaybookSequenceIcon,
    Check,
    X,
    Trash2,
    Pencil,
    TextInput,
    Modal
  },
  props: {
    playbooks: null,
    playbook: {
      type: Object,
      default: null
    }
  },
  emits: ["done", "cancel", "error"],
  data() {
    return {
      playbook_name: null,
      is_public: true,
      description: null,
      tag_name: "",
      all_tags: [],
      tag_to_be_deleted: null,
      tag_to_be_edited: null,
      playbook_tags: [],
      new_tag: {
        name: "",
        object_type: null,
        is_public: "t",
        description: "",
        color: "#FF4646"
      },
      selected_tags: [],
      is_name_duplicated: null,
      is_delete_tags_modal_open: false
    }
  },
  watch: {
    tag_name() {
      this.is_name_duplicated = false
      if (!this.tag_to_be_edited) {
        let arr = []
        this.all_tags = this.playbook_tags
        this.all_tags.map((t) => {
          if (t.name.toLowerCase() == this.tag_name.toLowerCase()) {
            this.is_name_duplicated = true
          }
          if (t.name.toLowerCase().includes(this.tag_name.toLowerCase())) {
            arr.push(t)
          }
        })
        this.all_tags = arr
      }
    }
  },
  created() {
    playbookService.getTags({ page_size: 100 }).then((res) => {
      this.playbook_tags = res.data.results

      this.all_tags = this.playbook_tags
    })

    if (this.playbook) {
      this.playbook_name = this.playbook.name
      this.is_public = this.playbook.is_public
      this.description = this.playbook.description
      this.selected_tags = this.playbook.tags
    }
  },
  methods: {
    createAndAssignTag() {
      this.new_tag.name = this.tag_name
      playbookService.createTag(this.new_tag).then((result) => {
        let tags = [result.data].concat(this.playbook_tags)
        this.$store.commit("playbook/setTags", tags)
        this.selected_tags.push(result.data.id)
        this.all_tags = this.playbook_tags
        this.tag_name = ""

        playbookService.getTags({ page_size: 100 }).then((res) => {
          this.playbook_tags = res.data.results

          this.all_tags = this.playbook_tags
        })
      })
    },
    editTag() {
      this.tag_to_be_edited.name = this.tag_name
      this.tag_to_be_edited.color = this.new_tag.color
      playbookService.updateTag(this.tag_to_be_edited.id, this.tag_to_be_edited).then(() => {
        let tags = this.playbook_tags
        let index = tags.findIndex((t) => t.id == this.tag_to_be_edited.id)
        tags[index].name = this.tag_to_be_edited.name
        tags[index].color = this.tag_to_be_edited.color
        this.$store.commit("playbook/setTags", tags)
        this.tag_to_be_edited = null
        this.tag_name = ""
      })
    },
    startSetup() {
      if (this.playbook) {
        //Edit playbook information
        this.$emitter.emit(ALL_EMITTERS.PLAYBOOK_EDITED, {
          name: this.playbook_name,
          is_public: this.is_public,
          description: this.description,
          tags: this.selected_tags
        })
      } else {
        //Put information to the new playbook
        this.$emitter.emit(ALL_EMITTERS.PLAYBOOK_TO_BE_CREATED, {
          name: this.playbook_name,
          is_public: this.is_public,
          description: this.description,
          tags: this.selected_tags
        })
      }
      this.$emit("done")
    },
    handleSelectTag(checked, tag) {
      if (checked) {
        this.selected_tags.push(tag.id)
      } else {
        let index = this.selected_tags.findIndex((r) => r === tag.id)
        this.selected_tags.splice(index, 1)
      }

      this.$store.dispatch("playbooks/updatePlaybook", { id: this.playbook.id, tags: this.selected_tags.map((t) => t.id) })
    },
    confirmDeleteTag(showIsDone) {
      playbookService.deleteTag(this.tag_to_be_deleted.id).then(() => {
        this.all_tags = this.playbook_tags.filter((tag) => tag.id != this.tag_to_be_deleted.id)
        this.$store.commit("playbook/setTags", this.all_tags)
        showIsDone()

        playbookService.getTags({ page_size: 100 }).then((res) => {
          this.playbook_tags = res.data.results

          this.all_tags = this.playbook_tags
        })

        this.$store.dispatch("playbooks/updatePlaybook", { id: this.playbook.id, tags: this.playbook_tags.map((t) => t.id) })
      })
    }
  }
}
</script>
